import React, {useMemo} from "react"
import {useTranslation} from "react-i18next"
import {KlassDropdown, KlassDropdownProps} from "../Select"
import {formatCodeName} from "helpers"
import {useAllCampuses} from "hooks"
import {v4 as uuid} from "uuid"
import {Campus} from "types/campus"
import {toLower} from "lodash"

type Props = Partial<KlassDropdownProps> & {
    displayOnlyCampusIds?: number[]
    displayOnlyActiveCampuses?: boolean
    additionalValues?: string[]
    ignoreCampusIds?: number[]
}

type CampusShortDetail = Pick<Campus.Campus, "name" | "code" | "isActive"> & {
    id: number | string
}

const CampusSelect: React.FC<Props> = ({
    displayOnlyCampusIds,
    displayOnlyActiveCampuses = false,
    additionalValues,
    ignoreCampusIds,
    ...props
}) => {
    const {t} = useTranslation(["common"])
    const {campuses} = useAllCampuses()

    const options = useMemo(() => {
        const newCampuses: CampusShortDetail[] = campuses.filter((campus) => {
            const filteredByIds = !displayOnlyCampusIds?.length || displayOnlyCampusIds.includes(campus.id)
            const filteredByIsActive = !displayOnlyActiveCampuses || campus.isActive
            const filteredByIgnoreIds = !ignoreCampusIds?.length || !ignoreCampusIds.includes(campus.id)
            return filteredByIds && filteredByIsActive && filteredByIgnoreIds
        })
        if (additionalValues?.length) {
            newCampuses.push(
                ...additionalValues.map((item, i) => ({
                    id: uuid(),
                    name: item,
                    isActive: true,
                    code: item
                }))
            )
        }

        return newCampuses
    }, [campuses, displayOnlyActiveCampuses, displayOnlyCampusIds, additionalValues, ignoreCampusIds])

    return (
        <KlassDropdown
            {...props}
            placeholder={props.placeholder || t("selectField.placeholder")}
            options={options}
            valueKey="id"
            labelKey="name"
            getOptionLabel={formatCodeName}
            filterOption={(option, inputValue) =>
                inputValue ? toLower(formatCodeName(option.data)).includes(toLower(inputValue)) : true
            }
        />
    )
}

export default CampusSelect
